import { useEffect, useRef } from "react";
import type { JSX } from "react";
import { Button, Flex, Message } from "@aws-amplify/ui-react";
import type {
  StorageBrowserType,
  UseView,
} from "@aws-amplify/ui-react-storage/browser";

import {
  BreadcrumbTrail,
  breadcrumbTrail,
  buildBreadcrumbs,
} from "../listing/files";
import { uploadRulesFor } from "../listing/folderTypes";
import { ListingLayout } from "../listing/layout";
import { ListingPagination } from "../listing/pagination";
import { UploadDropZone } from "./dropZone";
import { useUploadFilePicker } from "./filePicker";
import { uploadAcceptAttribute } from "./validation";
import {
  BACK_LABEL,
  CANCEL_LABEL,
  DONE_LABEL,
  rejectedFilesSummary,
  uploadLimitsLabel,
} from "./messages";

function useAutoStartUpload(
  taskCount: number,
  onActionStart: () => void,
): void {
  const hasStarted = useRef(false);

  useEffect(() => {
    if (hasStarted.current || taskCount === 0) {
      return;
    }
    hasStarted.current = true;
    onActionStart();
  }, [taskCount, onActionStart]);
}

/**
 * Build the replacement for the Storage Browser's own upload view.
 */
export function createUploadProgressView(
  useView: UseView,
  UploadView: StorageBrowserType["UploadView"],
  projectPrefixRoot: string,
): () => JSX.Element {
  function UploadProgressView(): JSX.Element {
    const uploadState = useView("Upload");
    const detailState = useView("LocationDetail");
    const { isProcessing, isProcessingComplete, tasks } = uploadState;
    const uploadRules = uploadRulesFor(uploadState.location);
    const { openFilePicker, filePickerInput } = useUploadFilePicker(
      uploadState.onDropFiles,
      uploadAcceptAttribute(uploadRules),
    );
    useAutoStartUpload(tasks.length, uploadState.onActionStart);

    const rejectedFiles = isProcessingComplete
      ? rejectedFilesSummary(uploadState.invalidFiles, uploadRules)
      : undefined;
    const canCancel = tasks.some((task) => task.cancel !== undefined);

    function leaveUploadView(navigate: () => void): void {
      uploadState.onActionExit();
      navigate();
    }

    function exitToFolder(): void {
      detailState.onRefresh();
      uploadState.onActionExit();
    }

    const crumbs = isProcessing
      ? breadcrumbTrail(uploadState.location, projectPrefixRoot)
      : buildBreadcrumbs(
          uploadState.location,
          () => {
            leaveUploadView(detailState.onNavigateHome);
          },
          (location, path) => {
            leaveUploadView(() => {
              detailState.onNavigate(location, path);
            });
          },
          projectPrefixRoot,
        );

    const cancelButton = (
      <Button isDisabled={!canCancel} onClick={uploadState.onActionCancel}>
        {CANCEL_LABEL}
      </Button>
    );

    const exitButton = (
      <Button onClick={exitToFolder} variation="primary">
        {isProcessingComplete ? DONE_LABEL : BACK_LABEL}
      </Button>
    );

    const rejectedFilesMessage = rejectedFiles ? (
      <Message colorTheme="error">{rejectedFiles}</Message>
    ) : null;

    const progressTable = (
      <Flex
        className="evido-listing__content"
        direction="column"
        flex="1"
        gap="0"
        minHeight="0"
      >
        <UploadView.TasksTable />
      </Flex>
    );

    const dropZoneCard = (
      <UploadView.DropZone>
        <UploadDropZone
          formatsLabel={uploadLimitsLabel(uploadRules)}
          onBrowse={openFilePicker}
        />
      </UploadView.DropZone>
    );

    return (
      <UploadView.Provider {...uploadState}>
        <ListingLayout
          controls={
            <ListingPagination
              hasNextPage={uploadState.hasNextPage}
              highestPageVisited={uploadState.highestPageVisited}
              itemLabel="Files"
              onPaginate={uploadState.onPaginate}
              page={uploadState.page}
              pageItemCount={tasks.length}
            />
          }
          footer={isProcessing ? cancelButton : exitButton}
          message={
            <Flex direction="column" gap="0.5rem">
              <UploadView.Message />
              {rejectedFilesMessage}
            </Flex>
          }
          title={<BreadcrumbTrail crumbs={crumbs} />}
        >
          {filePickerInput}
          <Flex
            borderColor="border.primary"
            borderRadius="large"
            borderStyle="solid"
            borderWidth="1px"
            className="evido-listing__panel evido-upload-listing__panel"
            direction="column"
            flex="1"
            gap="0"
            minHeight="0"
            minWidth="0"
            overflow="auto"
          >
            {tasks.length > 0 ? progressTable : dropZoneCard}
          </Flex>
        </ListingLayout>
      </UploadView.Provider>
    );
  }

  return UploadProgressView;
}
